import { Button, Flex, pxToRem, useStyles } from '@archipro-design/aria';
import { CheckLine, ChevronDown } from '@archipro-design/icons';
import { useClickAway } from 'ahooks';
import type { ReactNode, SyntheticEvent } from 'react';
import { useRef, useState } from 'react';
import * as S from './ManageDropdown.style';
import type { ManageDropdownDesign } from './ManageDropdown.style';

interface SelectionItem {
    id: string;
    render: ReactNode;
    onClick?: (event: SyntheticEvent) => void;
    type?: 'option' | 'action';
}

interface ManageDesignBoardDropdownProps {
    items: SelectionItem[];
    defaultSelectedIndex?: number;
    onSelect?: (id: string) => void;
    variant?: 'with-padding' | 'no-padding';
    noChevron?: boolean;
    design?: ManageDropdownDesign;
}

//#refactor: this component is a labyrinth of spaghetti making it hard to understand and maintain.
export const ManageDesignBoardDropdown = ({
    items,
    defaultSelectedIndex,
    onSelect,
    variant = 'with-padding',
    noChevron = false,
    design = {},
}: ManageDesignBoardDropdownProps) => {
    const { css } = useStyles({ design: { ...design } });

    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [selectedID, setSelectedID] = useState(
        (defaultSelectedIndex && items[defaultSelectedIndex]?.id) ||
            items[0]?.id ||
            0
    );
    const ref = useRef<HTMLButtonElement>(null);

    useClickAway(() => {
        setIsSelectOpen(false);
    }, ref);

    const onClickOption = (
        e: SyntheticEvent<HTMLElement, Event>,
        item: SelectionItem
    ) => {
        e.preventDefault();
        e.stopPropagation();
        //Action selections should not be selectable, they should handle their own clicks.
        switch (item.type) {
            case 'action':
                break;
            default:
                setSelectedID(item.id);
                onSelect && onSelect(item.id);
        }

        item?.onClick && item.onClick(e);

        setIsSelectOpen(false);
    };

    const selectedItem = items.find((item) => item.id === selectedID);

    return (
        <Flex className={css(S.PermissionContainer)}>
            {!isSelectOpen ? (
                <Button
                    type={'button'}
                    variant={'text'}
                    {...(variant === 'no-padding' && {
                        variables: { buttonPadding: 0 },
                    })}
                    size={14}
                    key={`manageItemSelected-${selectedID}`}
                    className={css(S.CollapsedDropDownButton)}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsSelectOpen(true);
                    }}
                    {...(design?.trigger?.padding && {
                        design: { padding: design.trigger.padding },
                    })}
                >
                    {selectedItem?.render ?? 'Select'}
                    {noChevron === false && (
                        <ChevronDown
                            variables={{
                                '1xSize': pxToRem(12),
                            }}
                        />
                    )}
                </Button>
            ) : (
                <div className={css(S.SelectPermissions)}>
                    {items.map((item: SelectionItem, index) => {
                        return (
                            <Button
                                ref={ref}
                                type={'button'}
                                color={'white'}
                                variant={'contained'}
                                size={14}
                                key={`manageOption-${item.id}-${index}`}
                                onClick={(e) => onClickOption(e, item)}
                            >
                                {item.render}
                                {item.id === selectedID && (
                                    <CheckLine
                                        variables={{
                                            '1xSize': pxToRem(12),
                                        }}
                                    />
                                )}
                            </Button>
                        );
                    })}
                </div>
            )}
        </Flex>
    );
};
